import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { TraderDialog } from "@/components/review/trader-dialog";
import { VesselDialog } from "@/components/review/vessel-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api";
import {
  directoryTradersQuery,
  directoryVesselsQuery,
  tradersApi,
  vesselsApi,
  type Trader,
  type TraderKind,
  type Vessel,
} from "@/lib/reference";

export const Route = createFileRoute("/_authenticated/directory")({
  component: DirectoryPage,
});

const PAGE_SIZE = 50;

/*
 * The shared directories behind the pickers: importers/exporters (with the CRIB
 * customs identifies them by) and vessels. New entries are usually added from a
 * bill's picker; this page is for looking one up, correcting it, or retiring it.
 */
function DirectoryPage() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Directory</h1>
        <p className="text-muted-foreground text-sm">
          Importers, exporters and vessels shared by every bill. Retired entries disappear from the
          pickers but stay on the bills that used them.
        </p>
      </div>
      <Tabs defaultValue="traders">
        <TabsList>
          <TabsTrigger value="traders">Traders</TabsTrigger>
          <TabsTrigger value="vessels">Vessels</TabsTrigger>
        </TabsList>
        <TabsContent value="traders">
          <TradersTab />
        </TabsContent>
        <TabsContent value="vessels">
          <VesselsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const KIND_LABEL: Record<TraderKind, string> = {
  importer: "Importer",
  exporter: "Exporter",
  both: "Importer & exporter",
};

function TradersTab() {
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<"" | TraderKind>("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Trader | null>(null);
  const list = useQuery(directoryTradersQuery(q, page, kind));
  const retire = useMutation({
    mutationFn: (trader: Trader) => tradersApi.update(trader.id, { is_active: false }),
    onSuccess: (t) => {
      void queryClient.invalidateQueries({ queryKey: ["reference", "traders"] });
      toast.success(`${t.name} retired from the directory`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Could not retire"),
  });
  const pages = Math.max(1, Math.ceil((list.data?.count ?? 0) / PAGE_SIZE));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search name, code or CRIB…"
          className="w-72"
        />
        <Select
          value={kind || "all"}
          onValueChange={(v) => {
            setKind(v === "all" ? "" : (v as TraderKind));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="importer">Importers</SelectItem>
            <SelectItem value="exporter">Exporters</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-muted-foreground text-sm">
          {list.data ? `${list.data.count.toLocaleString()} traders` : "…"}
        </span>
        <Button className="ml-auto" onClick={() => setCreating(true)}>
          New trader
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>CRIB</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Address</TableHead>
              <TableHead className="w-[1%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(list.data?.results ?? []).map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{KIND_LABEL[t.kind]}</Badge>
                </TableCell>
                <TableCell className="font-mono">
                  {t.crib_number || (
                    <span className="text-amber-700" title="Cannot be declared as consignee">
                      missing
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">{t.code}</TableCell>
                <TableCell
                  className="max-w-[24rem] truncate"
                  title={[t.address_1, t.address_2].filter(Boolean).join(", ")}
                >
                  {[t.address_1, t.address_2].filter(Boolean).join(", ")}
                </TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setEditing(t)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={retire.isPending}
                    onClick={() =>
                      confirm(`Retire ${t.name} from the directory?`) && retire.mutate(t)
                    }
                  >
                    Retire
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {list.data && list.data.results.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-6 text-center">
                  No traders match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} pages={pages} onPage={setPage} />
      <TraderDialog open={creating} onOpenChange={setCreating} onSaved={() => undefined} />
      <TraderDialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        trader={editing}
        onSaved={() => undefined}
      />
    </div>
  );
}

function VesselsTab() {
  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Vessel | null>(null);
  const list = useQuery(directoryVesselsQuery(q, page));
  const retire = useMutation({
    mutationFn: (vessel: Vessel) => vesselsApi.update(vessel.id, { is_active: false }),
    onSuccess: (v) => {
      void queryClient.invalidateQueries({ queryKey: ["reference", "vessels"] });
      toast.success(`${v.name} retired from the directory`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Could not retire"),
  });
  const pages = Math.max(1, Math.ceil((list.data?.count ?? 0) / PAGE_SIZE));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search vessel…"
          className="w-72"
        />
        <span className="text-muted-foreground text-sm">
          {list.data ? `${list.data.count.toLocaleString()} vessels` : "…"}
        </span>
        <Button className="ml-auto" onClick={() => setCreating(true)}>
          New vessel
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Flag</TableHead>
              <TableHead className="w-[1%]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(list.data?.results ?? []).map((v) => (
              <TableRow key={v.id}>
                <TableCell className="font-medium">{v.name}</TableCell>
                <TableCell className="font-mono">{v.nationality_code ?? "—"}</TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setEditing(v)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={retire.isPending}
                    onClick={() =>
                      confirm(`Retire ${v.name} from the directory?`) && retire.mutate(v)
                    }
                  >
                    Retire
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {list.data && list.data.results.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-muted-foreground py-6 text-center">
                  No vessels match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} pages={pages} onPage={setPage} />
      {creating && (
        <VesselDialog key="new" open onOpenChange={setCreating} onSaved={() => undefined} />
      )}
      {editing && (
        <VesselDialog
          key={editing.id}
          open
          onOpenChange={(open) => !open && setEditing(null)}
          vessel={editing}
          onSaved={() => undefined}
        />
      )}
    </div>
  );
}

function Pager({
  page,
  pages,
  onPage,
}: {
  page: number;
  pages: number;
  onPage: (p: number) => void;
}) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 text-sm">
      <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <span className="text-muted-foreground tabular-nums">
        {page} / {pages}
      </span>
      <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </div>
  );
}
